<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { FocusScope } from 'reka-ui'
import { inertOutside } from '@/util/inert_outside'

const props = withDefaults(defineProps<{ isInitializing?: boolean, label: string }>(), { isInitializing: true })

// Both shapes pin left and right so the overlay widens evenly from the centre; a width or an
// auto edge would jump instead of animating.
const INITIALIZING_SHAPE = 'inset-x-[calc(50%-6rem)] top-36 overflow-hidden rounded-3xl'
const OPEN_SHAPE = 'inset-6 overflow-auto'

const overlayShape = computed(() => (props.isInitializing ? INITIALIZING_SHAPE : OPEN_SHAPE))

const emit = defineEmits<{ close: [] }>()

const overlay = ref<InstanceType<typeof FocusScope> | null>(null)
let restorePageBehind: (() => void) | null = null

onMounted(() => {
  const overlayElement = overlay.value?.$el as HTMLElement | undefined
  if (overlayElement) {
    restorePageBehind = inertOutside(overlayElement)
  }
})

onBeforeUnmount(() => restorePageBehind?.())
</script>

<template>
  <Teleport to="body">
    <!-- Starts as a small pill and grows to fill the window, so opening it reads as an overlay
         on the page rather than a navigation away from it. -->
    <FocusScope
      ref="overlay"
      trapped
      loop
      role="dialog"
      aria-modal="true"
      :aria-label="label"
      :aria-busy="isInitializing"
      data-print-overlay
      data-testid="print-preview-overlay"
      class="fixed z-[100000] bg-white shadow-2xl transition-all duration-500 ease-out focus:outline-none motion-reduce:transition-none print:static print:overflow-visible print:text-black print:shadow-none"
      :class="overlayShape"
      @keydown.esc="emit('close')"
    >
      <slot />
    </FocusScope>
  </Teleport>
</template>

<style>
/* Print-only rules Tailwind can't express: the @page box, and hiding every other child of the
   body (the app, and any dialog left open beneath) so only the overlay prints. */
@media print {
  @page {
    size: 8.5in 11in;
    margin-top: 0.75in;
    orphans: 4;
    widows: 4;
  }

  body:has(> [data-print-overlay]) {
    margin-top: 15px;
    padding-right: 0 !important;
    overflow: visible !important;
  }

  body:has(> [data-print-overlay]) > :not([data-print-overlay]) {
    display: none !important;
  }
}
</style>
